import { useState, useCallback, useMemo, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { CreditCard, Calculator, RefreshCw, TrendingUp, Award, Split, LayoutGrid, Sparkles, Scale } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const brl = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const pct = (v) => `${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

const FORMAS_A_VISTA = [
  { value: 'pix', label: 'Pix' },
  { value: 'dinheiro', label: 'Dinheiro' },
  { value: 'transferencia', label: 'Transferência' },
  { value: 'debito', label: 'Débito' },
  { value: 'outra', label: 'Outra' },
];
const BANDEIRAS = [
  { value: 'mastercard', label: 'Mastercard' },
  { value: 'visa', label: 'Visa' },
  { value: 'elo', label: 'Elo' },
  { value: 'amex', label: 'Amex' },
];
const MAX_PARCELAS_MISTO = 10;

// ============================================================
// COMPARAÇÃO DE CENÁRIOS (2 lado-a-lado)
// ============================================================
function ScenariosComparison({ valorOriginal, setValorOriginal, bandeira, setBandeira, formaAvista, setFormaAvista }) {
  const [aAvista, setAAvista] = useState('0');
  const [aParcelas, setAParcelas] = useState(10);
  const [aLabel, setALabel] = useState('10x direto no cartão');
  const [bAvista, setBAvista] = useState('');
  const [bParcelas, setBParcelas] = useState(5);
  const [bLabel, setBLabel] = useState('Parte à vista + resto no cartão');
  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState(false);
  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const comparar = async () => {
    const vo = parseFloat(valorOriginal) || 0;
    if (vo <= 0) { toast.error('Informe o valor original'); return; }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/card-simulator/compare-scenarios`, {
        valor_original: vo,
        forma_a_vista: formaAvista,
        bandeira,
        cenario_a_valor_a_vista: parseFloat(aAvista) || 0,
        cenario_a_parcelas: parseInt(aParcelas) || 1,
        cenario_a_label: aLabel,
        cenario_b_valor_a_vista: parseFloat(bAvista) || 0,
        cenario_b_parcelas: parseInt(bParcelas) || 1,
        cenario_b_label: bLabel,
      }, { headers: authHeaders() });
      setResultado(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao comparar cenários');
    } finally {
      setLoading(false);
    }
  };

  const renderCenario = (c, isBest) => {
    const cartao = c.cartao;
    return (
      <Card className={isBest ? 'border-green-500/50 bg-green-500/5' : ''}>
        <CardHeader>
          <CardTitle className="text-base flex items-center justify-between">
            <span>{c.label}</span>
            {isBest && <Badge className="bg-green-600">Melhor p/ empresa</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground">Valor original</span><span className="font-semibold">{brl(c.valor_original)}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">À vista ({c.a_vista.forma.toUpperCase()})</span><span className="font-semibold">{brl(c.a_vista.valor)}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Desconto gerado</span><span className="text-green-700 font-semibold">- {brl(c.a_vista.desconto_gerado)}</span></div>
          {cartao && (
            <>
              <div className="flex justify-between"><span className="text-muted-foreground">Cartão original</span><span>{brl(cartao.valor_original_cartao)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Cartão final</span><span className="font-semibold">{brl(cartao.valor_final_cartao)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">{cartao.parcelas}× de</span><span>{brl(cartao.valor_parcela)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Taxa efetiva ({pct(cartao.taxa_percentual)})</span><span className="text-orange-600">{brl(cartao.taxa_efetiva_valor)}</span></div>
            </>
          )}
          <div className="border-t pt-2 flex justify-between font-semibold"><span>Cliente paga</span><span>{brl(c.resultado.total_pago_cliente)}</span></div>
          <div className="flex justify-between text-base font-bold text-green-700"><span>Líquido empresa</span><span>{brl(c.resultado.valor_liquido_total)}</span></div>
        </CardContent>
      </Card>
    );
  };

  const isB = resultado && (resultado.diferencas?.diferenca_liquido_empresa || 0) > 0;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Scale className="h-4 w-4" />Comparar cenários lado a lado</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Valor original (R$)</Label>
            <Input type="number" step="0.01" value={valorOriginal}
              onChange={(e) => setValorOriginal(e.target.value)} data-testid="sc-input-valor-original" />
          </div>
          <div>
            <Label className="text-xs">Bandeira</Label>
            <Select value={bandeira} onValueChange={setBandeira}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{BANDEIRAS.map(b => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Forma à vista</Label>
            <Select value={formaAvista} onValueChange={setFormaAvista}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{FORMAS_A_VISTA.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-sm">Cenário A</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label className="text-xs">Rótulo</Label>
              <Input value={aLabel} onChange={(e) => setALabel(e.target.value)} data-testid="sc-a-label" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Valor à vista (R$)</Label>
                <Input type="number" step="0.01" value={aAvista} onChange={(e) => setAAvista(e.target.value)} data-testid="sc-a-avista" />
              </div>
              <div>
                <Label className="text-xs">Parcelas do cartão</Label>
                <Select value={String(aParcelas)} onValueChange={(v) => setAParcelas(parseInt(v))}>
                  <SelectTrigger data-testid="sc-a-parcelas"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: MAX_PARCELAS_MISTO }).map((_, i) => (
                      <SelectItem key={i + 1} value={String(i + 1)}>{i + 1}x</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-sm">Cenário B</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label className="text-xs">Rótulo</Label>
              <Input value={bLabel} onChange={(e) => setBLabel(e.target.value)} data-testid="sc-b-label" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Valor à vista (R$)</Label>
                <Input type="number" step="0.01" value={bAvista} onChange={(e) => setBAvista(e.target.value)} data-testid="sc-b-avista" />
              </div>
              <div>
                <Label className="text-xs">Parcelas do cartão</Label>
                <Select value={String(bParcelas)} onValueChange={(v) => setBParcelas(parseInt(v))}>
                  <SelectTrigger data-testid="sc-b-parcelas"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: MAX_PARCELAS_MISTO }).map((_, i) => (
                      <SelectItem key={i + 1} value={String(i + 1)}>{i + 1}x</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex justify-end">
        <Button onClick={comparar} disabled={loading} data-testid="sc-btn-comparar" size="lg">
          <Scale className="h-4 w-4 mr-2" />{loading ? 'Comparando...' : 'Comparar cenários'}
        </Button>
      </div>

      {resultado && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderCenario(resultado.cenario_a, !isB)}
            {renderCenario(resultado.cenario_b, isB)}
          </div>
          <Card className="border-primary/40 bg-primary/5">
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Award className="h-4 w-4" />Diferenças ({resultado.cenario_b.label} - {resultado.cenario_a.label})</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div>
                <div className="text-xs text-muted-foreground">Líquido empresa</div>
                <div className={`font-bold ${resultado.diferencas.diferenca_liquido_empresa >= 0 ? 'text-green-700' : 'text-red-600'}`} data-testid="sc-dif-liquido">
                  {resultado.diferencas.diferenca_liquido_empresa >= 0 ? '+' : ''}{brl(resultado.diferencas.diferenca_liquido_empresa)}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Cliente paga</div>
                <div className={`font-bold ${resultado.diferencas.diferenca_total_pago_cliente <= 0 ? 'text-green-700' : 'text-orange-600'}`}>
                  {resultado.diferencas.diferenca_total_pago_cliente >= 0 ? '+' : ''}{brl(resultado.diferencas.diferenca_total_pago_cliente)}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Desconto p/ cliente</div>
                <div className="font-bold text-green-700">+{brl(resultado.diferencas.diferenca_desconto_cliente)}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Taxa do cartão</div>
                <div className="font-bold text-orange-600">{brl(resultado.diferencas.diferenca_taxa_cartao)}</div>
              </div>
              <div className="col-span-full text-sm font-semibold" data-testid="sc-recomendacao">
                💡 {resultado.recomendacao_heuristica}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

// ============================================================
// ANÁLISE IA DA PROPOSTA
// ============================================================
function AiAnalysisPanel({ valorOriginal, setValorOriginal, bandeira, setBandeira, formaAvista, setFormaAvista, valorAvista, setValorAvista, parcelas, setParcelas }) {
  const [valorOferecido, setValorOferecido] = useState('');
  const [custoProdutos, setCustoProdutos] = useState('');
  const [frete, setFrete] = useState('');
  const [montagem, setMontagem] = useState('');
  const [comissao, setComissao] = useState('');
  const [outrosCustos, setOutrosCustos] = useState('');
  const [freteGratis, setFreteGratis] = useState(false);
  const [montagemGratis, setMontagemGratis] = useState(false);
  const [prazoEntrega, setPrazoEntrega] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [loading, setLoading] = useState(false);
  const [analise, setAnalise] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const classCor = (c) => {
    if (!c) return 'bg-muted';
    if (c === 'MUITO_BOA' || c === 'BOA_ACEITAVEL') return 'bg-green-600 text-white';
    if (c === 'ACEITAVEL_COM_ATENCAO') return 'bg-yellow-500 text-white';
    if (c === 'CONTRAPROPOSTA_RECOMENDADA') return 'bg-orange-500 text-white';
    if (c === 'PRECISA_APROVACAO') return 'bg-blue-600 text-white';
    return 'bg-red-600 text-white';
  };

  const analisar = async () => {
    const vo = parseFloat(valorOriginal) || 0;
    if (vo <= 0) { toast.error('Informe o valor original da venda'); return; }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/card-simulator/analyze`, {
        valor_original: vo,
        valor_oferecido: parseFloat(valorOferecido) || vo,
        valor_a_vista: parseFloat(valorAvista) || 0,
        forma_a_vista: formaAvista,
        bandeira,
        parcelas: parseInt(parcelas) || 1,
        custo_produtos: parseFloat(custoProdutos) || 0,
        frete: parseFloat(frete) || 0,
        montagem: parseFloat(montagem) || 0,
        comissao: parseFloat(comissao) || 0,
        outros_custos: parseFloat(outrosCustos) || 0,
        frete_gratis: freteGratis,
        montagem_gratis: montagemGratis,
        prazo_entrega_solicitado: prazoEntrega,
        observacoes_cliente: observacoes,
      }, { headers: authHeaders(), timeout: 120000 });
      setAnalise(r.data);
      setSheetOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro na análise');
    } finally {
      setLoading(false);
    }
  };

  const copiarMensagem = () => {
    const msg = analise?.analise_ia?.mensagem_cliente;
    if (!msg) return;
    navigator.clipboard.writeText(msg);
    toast.success('Mensagem copiada para a área de transferência');
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" />Proposta do cliente</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Valor original da venda (R$)</Label>
            <Input type="number" step="0.01" value={valorOriginal}
              onChange={(e) => setValorOriginal(e.target.value)} data-testid="ai-valor-original" />
          </div>
          <div>
            <Label className="text-xs">Valor oferecido pelo cliente (R$)</Label>
            <Input type="number" step="0.01" value={valorOferecido}
              onChange={(e) => setValorOferecido(e.target.value)} placeholder="ou deixe igual ao original"
              data-testid="ai-valor-oferecido" />
          </div>
          <div>
            <Label className="text-xs">Prazo de entrega solicitado</Label>
            <Input value={prazoEntrega} onChange={(e) => setPrazoEntrega(e.target.value)}
              placeholder="ex: 15 dias" data-testid="ai-prazo" />
          </div>
          <div>
            <Label className="text-xs">Valor à vista (R$)</Label>
            <Input type="number" step="0.01" value={valorAvista}
              onChange={(e) => setValorAvista(e.target.value)} data-testid="ai-avista" />
          </div>
          <div>
            <Label className="text-xs">Forma à vista</Label>
            <Select value={formaAvista} onValueChange={setFormaAvista}>
              <SelectTrigger data-testid="ai-forma"><SelectValue /></SelectTrigger>
              <SelectContent>{FORMAS_A_VISTA.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Bandeira</Label>
            <Select value={bandeira} onValueChange={setBandeira}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{BANDEIRAS.map(b => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Parcelas do cartão</Label>
            <Select value={String(parcelas)} onValueChange={(v) => setParcelas(parseInt(v))}>
              <SelectTrigger data-testid="ai-parcelas"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Array.from({ length: MAX_PARCELAS_MISTO }).map((_, i) => (
                  <SelectItem key={i + 1} value={String(i + 1)}>{i + 1}x</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2 lg:col-span-3">
            <Label className="text-xs">Observações do cliente</Label>
            <Textarea rows={2} value={observacoes} onChange={(e) => setObservacoes(e.target.value)}
              placeholder="Ex: Cliente aceita fechar hoje com 8% de desconto..."
              data-testid="ai-obs" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Custos internos (para cálculo de margem)</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <div>
            <Label className="text-xs">Custo dos produtos (R$)</Label>
            <Input type="number" step="0.01" value={custoProdutos}
              onChange={(e) => setCustoProdutos(e.target.value)} data-testid="ai-custo-produtos" />
          </div>
          <div>
            <Label className="text-xs">Frete (R$)</Label>
            <Input type="number" step="0.01" value={frete}
              onChange={(e) => setFrete(e.target.value)} data-testid="ai-frete" />
          </div>
          <div>
            <Label className="text-xs">Montagem (R$)</Label>
            <Input type="number" step="0.01" value={montagem}
              onChange={(e) => setMontagem(e.target.value)} data-testid="ai-montagem" />
          </div>
          <div>
            <Label className="text-xs">Comissão (R$)</Label>
            <Input type="number" step="0.01" value={comissao}
              onChange={(e) => setComissao(e.target.value)} data-testid="ai-comissao" />
          </div>
          <div>
            <Label className="text-xs">Outros custos (R$)</Label>
            <Input type="number" step="0.01" value={outrosCustos}
              onChange={(e) => setOutrosCustos(e.target.value)} data-testid="ai-outros" />
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={freteGratis} onChange={(e) => setFreteGratis(e.target.checked)} data-testid="ai-frete-gratis" />
            Frete grátis solicitado
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={montagemGratis} onChange={(e) => setMontagemGratis(e.target.checked)} data-testid="ai-montagem-gratis" />
            Montagem grátis solicitada
          </label>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={analisar} disabled={loading} size="lg" data-testid="ai-btn-analisar">
          <Sparkles className="h-4 w-4 mr-2" />{loading ? 'Analisando com IA...' : 'Analisar proposta com IA'}
        </Button>
      </div>

      {/* Painel lateral com o resultado */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />Análise da Proposta
            </SheetTitle>
            <SheetDescription>
              Cálculos determinísticos do sistema + interpretação e sugestões pela IA. A IA nunca inventa números.
            </SheetDescription>
          </SheetHeader>
          {analise && (
            <div className="space-y-4 mt-4" data-testid="ai-result-panel">
              {/* Classificação */}
              <div className="flex flex-wrap items-center gap-2">
                <Badge className={classCor(analise.analise_ia?.classificacao) + ' text-sm'} data-testid="ai-classificacao">
                  {(analise.analise_ia?.classificacao || '').replace(/_/g, ' ')}
                </Badge>
                <Badge variant="outline">Decisão: {analise.analise_ia?.decisao_sugerida}</Badge>
                <Badge variant="secondary">Confiança: {analise.analise_ia?.nivel_confianca}</Badge>
                {analise.analise_ia?._fallback && <Badge variant="destructive">Fallback (IA indisponível)</Badge>}
              </div>

              {/* Resumo */}
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Resumo</CardTitle></CardHeader>
                <CardContent className="text-sm">{analise.analise_ia?.resumo}</CardContent>
              </Card>

              {/* Cálculos do sistema */}
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Números do sistema</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-muted-foreground">Valor final:</span> <strong>{brl(analise.calculos.valor_final_venda)}</strong></div>
                  <div><span className="text-muted-foreground">Desconto gerado:</span> <strong>{brl(analise.calculos.desconto_gerado_pelo_pagamento_a_vista)}</strong></div>
                  <div><span className="text-muted-foreground">Cartão original:</span> {brl(analise.calculos.valor_original_cartao)}</div>
                  <div><span className="text-muted-foreground">Cartão final:</span> <strong>{brl(analise.calculos.valor_final_cartao)}</strong></div>
                  <div><span className="text-muted-foreground">Taxa efetiva:</span> {brl(analise.calculos.taxa_efetiva_cartao)}</div>
                  <div><span className="text-muted-foreground">Cliente paga:</span> <strong>{brl(analise.calculos.total_pago_cliente)}</strong></div>
                  <div><span className="text-muted-foreground">Líquido total:</span> <strong className="text-green-700">{brl(analise.calculos.valor_liquido_total)}</strong></div>
                  <div><span className="text-muted-foreground">Lucro estimado:</span> <strong>{brl(analise.calculos.lucro_estimado)}</strong></div>
                  <div><span className="text-muted-foreground">Margem:</span> <strong>{pct(analise.calculos.margem_percentual)}</strong></div>
                </CardContent>
              </Card>

              {/* Pontos positivos */}
              {analise.analise_ia?.pontos_positivos?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm text-green-700">✓ Pontos positivos</CardTitle></CardHeader>
                  <CardContent><ul className="text-sm space-y-1 list-disc list-inside">
                    {analise.analise_ia.pontos_positivos.map((p, i) => <li key={i}>{p}</li>)}
                  </ul></CardContent>
                </Card>
              )}

              {/* Pontos de atenção */}
              {analise.analise_ia?.pontos_atencao?.length > 0 && (
                <Card className="border-yellow-500/40">
                  <CardHeader className="pb-2"><CardTitle className="text-sm text-yellow-700">⚠ Pontos de atenção</CardTitle></CardHeader>
                  <CardContent><ul className="text-sm space-y-1 list-disc list-inside">
                    {analise.analise_ia.pontos_atencao.map((p, i) => <li key={i}>{p}</li>)}
                  </ul></CardContent>
                </Card>
              )}

              {/* Motivos */}
              {analise.analise_ia?.motivos?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Motivos da classificação</CardTitle></CardHeader>
                  <CardContent><ul className="text-sm space-y-1 list-disc list-inside">
                    {analise.analise_ia.motivos.map((p, i) => <li key={i}>{p}</li>)}
                  </ul></CardContent>
                </Card>
              )}

              {/* Contrapropostas */}
              {analise.analise_ia?.contrapropostas?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Contrapropostas sugeridas</CardTitle></CardHeader>
                  <CardContent className="space-y-3">
                    {analise.analise_ia.contrapropostas.map((cp, i) => (
                      <div key={i} className="border rounded p-3 text-sm space-y-1" data-testid={`ai-contra-${i}`}>
                        <div className="flex items-center justify-between">
                          <span className="font-semibold">{cp.titulo}</span>
                          <Badge variant="outline" className="text-xs">{cp.tipo?.replace(/_/g, ' ')}</Badge>
                        </div>
                        {cp.descricao && <div>{cp.descricao}</div>}
                        {cp.justificativa && <div className="text-xs text-muted-foreground italic">{cp.justificativa}</div>}
                        {cp.exige_aprovacao && <Badge variant="destructive" className="text-xs">Exige aprovação do administrador</Badge>}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Mensagem para o cliente */}
              {analise.analise_ia?.mensagem_cliente && (
                <Card className="border-primary/40">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center justify-between">
                      <span>Mensagem para o cliente (WhatsApp)</span>
                      <Button size="sm" variant="outline" onClick={copiarMensagem} data-testid="ai-btn-copiar-msg">Copiar</Button>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-sm whitespace-pre-wrap bg-muted/50 rounded p-3" data-testid="ai-msg-cliente">
                      {analise.analise_ia.mensagem_cliente}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Dados ausentes */}
              {analise.dados_ausentes?.length > 0 && (
                <div className="text-xs text-muted-foreground">
                  <strong>Análise parcial:</strong> dados ausentes: {analise.dados_ausentes.join(', ')}
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function MixedPaymentSection() {
  const [subTab, setSubTab] = useState('simulate'); // simulate | reverse | compare | scenarios | maxDiscount
  const [valorOriginal, setValorOriginal] = useState('');
  const [descontoPct, setDescontoPct] = useState('');
  const [descontoValor, setDescontoValor] = useState('');
  const [valorAvista, setValorAvista] = useState('');
  const [percentualAvista, setPercentualAvista] = useState('');
  const [formaAvista, setFormaAvista] = useState('pix');
  const [taxaAvistaPct, setTaxaAvistaPct] = useState('');
  const [bandeira, setBandeira] = useState('mastercard');
  const [parcelas, setParcelas] = useState(1);
  // Reverso
  const [liqDesejado, setLiqDesejado] = useState('');
  // Max discount
  const [liqMinimo, setLiqMinimo] = useState('');
  // Compare
  const [percsCompare, setPercsCompare] = useState('10,20,30,40,50');
  const [parcMaxCompare, setParcMaxCompare] = useState(MAX_PARCELAS_MISTO);

  const [resultado, setResultado] = useState(null);
  const [resultadoReverso, setResultadoReverso] = useState(null);
  const [resultadoMaxDisc, setResultadoMaxDisc] = useState(null);
  const [resultadoCompare, setResultadoCompare] = useState(null);
  const [loading, setLoading] = useState(false);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  // Sincronização recíproca valor/% à vista
  const onValorAvista = (v) => {
    setValorAvista(v);
    const vo = parseFloat(valorOriginal) || 0;
    const dv = parseFloat(descontoValor) || (parseFloat(descontoPct) ? vo * parseFloat(descontoPct) / 100 : 0);
    const vf = Math.max(0, vo - dv);
    const av = parseFloat(v) || 0;
    setPercentualAvista(vf > 0 && av > 0 ? ((av / vf) * 100).toFixed(2) : '');
  };
  const onPercentualAvista = (v) => {
    setPercentualAvista(v);
    const vo = parseFloat(valorOriginal) || 0;
    const dv = parseFloat(descontoValor) || (parseFloat(descontoPct) ? vo * parseFloat(descontoPct) / 100 : 0);
    const vf = Math.max(0, vo - dv);
    const p = parseFloat(v) || 0;
    setValorAvista(vf > 0 && p > 0 ? ((vf * p) / 100).toFixed(2) : '');
  };
  const onDescPct = (v) => {
    setDescontoPct(v);
    const vo = parseFloat(valorOriginal) || 0;
    const p = parseFloat(v) || 0;
    setDescontoValor(vo > 0 && p > 0 ? (vo * p / 100).toFixed(2) : '');
  };
  const onDescValor = (v) => {
    setDescontoValor(v);
    const vo = parseFloat(valorOriginal) || 0;
    const dv = parseFloat(v) || 0;
    setDescontoPct(vo > 0 && dv > 0 ? ((dv / vo) * 100).toFixed(2) : '');
  };

  const valorFinalPreview = useMemo(() => {
    const vo = parseFloat(valorOriginal) || 0;
    const dv = parseFloat(descontoValor) || 0;
    const dp = parseFloat(descontoPct) || 0;
    if (dv > 0) return Math.max(0, vo - dv);
    if (dp > 0) return Math.max(0, vo - (vo * dp / 100));
    return vo;
  }, [valorOriginal, descontoValor, descontoPct]);

  const simular = useCallback(async () => {
    const vo = parseFloat(valorOriginal) || 0;
    if (vo <= 0) return;
    setLoading(true);
    try {
      const body = {
        valor_original: vo,
        desconto_percentual: parseFloat(descontoPct) || 0,
        desconto_valor: parseFloat(descontoValor) || 0,
        valor_a_vista: parseFloat(valorAvista) || 0,
        percentual_a_vista: parseFloat(percentualAvista) || 0,
        forma_a_vista: formaAvista,
        taxa_a_vista_percentual: parseFloat(taxaAvistaPct) || 0,
        bandeira,
        parcelas: parseInt(parcelas) || 1,
      };
      const r = await axios.post(`${API}/card-simulator/mixed-simulate`, body, { headers: authHeaders() });
      setResultado(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao simular');
    } finally {
      setLoading(false);
    }
  }, [valorOriginal, descontoPct, descontoValor, valorAvista, percentualAvista, formaAvista,
      taxaAvistaPct, bandeira, parcelas]);

  useEffect(() => {
    if (subTab === 'simulate' && (parseFloat(valorOriginal) || 0) > 0) {
      const t = setTimeout(() => simular(), 400);
      return () => clearTimeout(t);
    }
  }, [valorOriginal, descontoPct, descontoValor, valorAvista, percentualAvista, formaAvista,
      taxaAvistaPct, bandeira, parcelas, subTab, simular]);

  const simularReverso = async () => {
    const liq = parseFloat(liqDesejado) || 0;
    const av = parseFloat(valorAvista) || 0;
    if (liq <= 0) { toast.error('Informe o líquido total desejado'); return; }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/card-simulator/mixed-reverse`, {
        valor_liquido_total_desejado: liq,
        valor_a_vista: av,
        forma_a_vista: formaAvista,
        taxa_a_vista_percentual: parseFloat(taxaAvistaPct) || 0,
        bandeira,
        parcelas: parseInt(parcelas) || 1,
      }, { headers: authHeaders() });
      setResultadoReverso(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao calcular reverso');
    } finally {
      setLoading(false);
    }
  };

  const calcularMaxDisc = async () => {
    const vo = parseFloat(valorOriginal) || 0;
    const lm = parseFloat(liqMinimo) || 0;
    if (vo <= 0 || lm <= 0) { toast.error('Preencha valor original e líquido mínimo'); return; }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/card-simulator/max-discount`, {
        valor_original: vo,
        valor_a_vista: parseFloat(valorAvista) || 0,
        percentual_a_vista: parseFloat(percentualAvista) || 0,
        forma_a_vista: formaAvista,
        taxa_a_vista_percentual: parseFloat(taxaAvistaPct) || 0,
        bandeira,
        parcelas: parseInt(parcelas) || 1,
        valor_liquido_minimo: lm,
      }, { headers: authHeaders() });
      setResultadoMaxDisc(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao calcular desconto máximo');
    } finally {
      setLoading(false);
    }
  };

  const compararEntradas = async () => {
    const vo = parseFloat(valorOriginal) || 0;
    if (vo <= 0) { toast.error('Informe o valor original'); return; }
    const percs = percsCompare.split(',').map(s => parseFloat(s.trim())).filter(x => !isNaN(x) && x >= 0 && x <= 100);
    setLoading(true);
    try {
      const r = await axios.post(`${API}/card-simulator/mixed-compare`, {
        valor_original: vo,
        desconto_percentual: parseFloat(descontoPct) || 0,
        desconto_valor: parseFloat(descontoValor) || 0,
        forma_a_vista: formaAvista,
        taxa_a_vista_percentual: parseFloat(taxaAvistaPct) || 0,
        bandeira,
        percentuais_entrada: percs.length ? percs : [10, 20, 30, 40, 50],
        parcelas_max: parseInt(parcMaxCompare) || MAX_PARCELAS_MISTO,
      }, { headers: authHeaders() });
      setResultadoCompare(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao comparar');
    } finally {
      setLoading(false);
    }
  };

  const commonDadosVenda = (
    <Card>
      <CardHeader><CardTitle className="text-base">Dados da venda</CardTitle></CardHeader>
      <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <div>
          <Label className="text-xs">Valor original (R$)</Label>
          <Input type="number" step="0.01" placeholder="0,00" value={valorOriginal}
            onChange={(e) => setValorOriginal(e.target.value)} data-testid="mix-input-valor-original" />
        </div>
        <div>
          <Label className="text-xs">Desconto (%)</Label>
          <Input type="number" step="0.01" placeholder="0" value={descontoPct}
            onChange={(e) => onDescPct(e.target.value)} data-testid="mix-input-desc-pct" />
        </div>
        <div>
          <Label className="text-xs">Desconto (R$)</Label>
          <Input type="number" step="0.01" placeholder="0,00" value={descontoValor}
            onChange={(e) => onDescValor(e.target.value)} data-testid="mix-input-desc-valor" />
        </div>
        <div className="sm:col-span-2 lg:col-span-3 text-sm">
          Valor final após desconto: <span className="font-semibold" data-testid="mix-valor-final">{brl(valorFinalPreview)}</span>
        </div>
      </CardContent>
    </Card>
  );

  const commonPartes = (
    <Card>
      <CardHeader><CardTitle className="text-base">Divisão à vista + cartão</CardTitle></CardHeader>
      <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <Label className="text-xs">Valor à vista (R$)</Label>
          <Input type="number" step="0.01" placeholder="0,00" value={valorAvista}
            onChange={(e) => onValorAvista(e.target.value)} data-testid="mix-input-avista-valor" />
        </div>
        <div>
          <Label className="text-xs">% à vista</Label>
          <Input type="number" step="0.01" placeholder="0" value={percentualAvista}
            onChange={(e) => onPercentualAvista(e.target.value)} data-testid="mix-input-avista-pct" />
        </div>
        <div>
          <Label className="text-xs">Forma à vista</Label>
          <Select value={formaAvista} onValueChange={setFormaAvista}>
            <SelectTrigger data-testid="mix-select-forma-avista"><SelectValue /></SelectTrigger>
            <SelectContent>
              {FORMAS_A_VISTA.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Taxa da forma à vista (%)</Label>
          <Input type="number" step="0.01" placeholder="0" value={taxaAvistaPct}
            onChange={(e) => setTaxaAvistaPct(e.target.value)}
            disabled={formaAvista === 'pix' || formaAvista === 'dinheiro' || formaAvista === 'transferencia'}
            data-testid="mix-input-taxa-avista" />
        </div>
        <div>
          <Label className="text-xs">Bandeira do cartão</Label>
          <Select value={bandeira} onValueChange={setBandeira}>
            <SelectTrigger data-testid="mix-select-bandeira"><SelectValue /></SelectTrigger>
            <SelectContent>
              {BANDEIRAS.map(b => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Parcelas</Label>
          <Select value={String(parcelas)} onValueChange={(v) => setParcelas(parseInt(v))}>
            <SelectTrigger data-testid="mix-select-parcelas"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Array.from({ length: MAX_PARCELAS_MISTO }).map((_, i) => (
                <SelectItem key={i + 1} value={String(i + 1)}>{i + 1}x</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2 lg:col-span-4 text-xs text-muted-foreground p-2 bg-blue-50 dark:bg-blue-950/20 rounded border border-blue-200 dark:border-blue-800">
          <strong>Desconto gerado pelo pagamento à vista:</strong> o valor pago à vista gera um desconto equivalente à taxa do parcelamento escolhido. Esse desconto será aplicado sobre o valor cobrado no cartão. O valor à vista não é reduzido.
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-4">
      <Tabs value={subTab} onValueChange={setSubTab}>
        <TabsList className="grid grid-cols-3 md:grid-cols-6 max-w-4xl">
          <TabsTrigger value="simulate" data-testid="mix-tab-simulate">Simular</TabsTrigger>
          <TabsTrigger value="reverse" data-testid="mix-tab-reverse">Reverso</TabsTrigger>
          <TabsTrigger value="compare" data-testid="mix-tab-compare"><LayoutGrid className="h-4 w-4 mr-1" />Comparar</TabsTrigger>
          <TabsTrigger value="scenarios" data-testid="mix-tab-scenarios"><Scale className="h-4 w-4 mr-1" />Cenários</TabsTrigger>
          <TabsTrigger value="ai" data-testid="mix-tab-ai"><Sparkles className="h-4 w-4 mr-1" />Análise IA</TabsTrigger>
          <TabsTrigger value="maxDiscount" data-testid="mix-tab-maxdisc">Máx. desc.</TabsTrigger>
        </TabsList>

        {/* ==== SUB-ABA SIMULAR ==== */}
        <TabsContent value="simulate" className="space-y-4">
          {commonDadosVenda}
          {commonPartes}

          {resultado && (
            <>
              {/* Bloco resumo da venda */}
              <Card className="border-primary/30">
                <CardHeader><CardTitle className="text-base">Resumo da venda</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  <div><div className="text-xs text-muted-foreground">Valor original</div><div className="font-semibold">{brl(resultado.valor_original)}</div></div>
                  <div><div className="text-xs text-muted-foreground">Desconto manual</div><div className="font-semibold">{brl(resultado.desconto_valor)} ({pct(resultado.desconto_percentual)})</div></div>
                  <div><div className="text-xs text-muted-foreground">Valor final da venda</div><div className="font-semibold text-primary">{brl(resultado.valor_final)}</div></div>
                  <div><div className="text-xs text-muted-foreground">Total pago pelo cliente</div><div className="font-bold text-lg" data-testid="mix-valor-pago-cliente">{brl(resultado.resultado.total_pago_cliente)}</div></div>
                </CardContent>
              </Card>

              {/* À vista */}
              <Card>
                <CardHeader><CardTitle className="text-base">Parte à vista</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
                  <div><div className="text-xs text-muted-foreground">Forma</div><div className="font-semibold uppercase">{resultado.a_vista.forma}</div></div>
                  <div><div className="text-xs text-muted-foreground">Valor pago à vista</div><div className="font-semibold" data-testid="mix-liq-avista">{brl(resultado.a_vista.valor)}</div></div>
                  <div><div className="text-xs text-muted-foreground">% do total</div><div className="font-semibold">{pct(resultado.a_vista.percentual)}</div></div>
                  <div><div className="text-xs text-muted-foreground">Taxa parcelamento usada</div><div className="font-semibold">{pct(resultado.a_vista.taxa_parcelamento_usada_desconto)}</div></div>
                  <div><div className="text-xs text-muted-foreground">Desconto gerado</div><div className="font-bold text-green-600" data-testid="mix-desconto-gerado">{brl(resultado.a_vista.desconto_gerado)}</div></div>
                </CardContent>
              </Card>

              {/* Cartão */}
              {resultado.cartao && (
                <Card>
                  <CardHeader><CardTitle className="text-base">Parte no cartão</CardTitle></CardHeader>
                  <CardContent className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 text-sm">
                    <div><div className="text-xs text-muted-foreground">Original cartão</div><div className="font-semibold">{brl(resultado.cartao.valor_original_cartao)}</div></div>
                    <div><div className="text-xs text-muted-foreground">Desconto aplicado</div><div className="font-semibold text-green-600">- {brl(resultado.cartao.desconto_aplicado)}</div></div>
                    <div><div className="text-xs text-muted-foreground">Valor final cartão</div><div className="font-bold" data-testid="mix-valor-final-cartao">{brl(resultado.cartao.valor_final_cartao)}</div></div>
                    <div><div className="text-xs text-muted-foreground">Bandeira</div><div className="font-semibold uppercase">{resultado.cartao.bandeira}</div></div>
                    <div><div className="text-xs text-muted-foreground">Parcelas</div><div className="font-semibold">{resultado.cartao.parcelas}× {brl(resultado.cartao.valor_parcela)}</div></div>
                    <div><div className="text-xs text-muted-foreground">Última parcela</div><div className="font-semibold">{brl(resultado.cartao.valor_ultima_parcela)}</div></div>
                    <div><div className="text-xs text-muted-foreground">Taxa</div><div className="font-semibold text-orange-600">{pct(resultado.cartao.taxa_percentual)} = {brl(resultado.cartao.taxa_efetiva_valor)}</div></div>
                    <div><div className="text-xs text-muted-foreground">Líquido cartão</div><div className="font-bold text-green-600">{brl(resultado.cartao.valor_liquido)}</div></div>
                    {resultado.cartao.alerta_desconto_excede && (
                      <div className="col-span-full bg-red-50 dark:bg-red-950/20 border border-red-300 dark:border-red-800 text-red-700 dark:text-red-300 p-2 rounded text-xs">
                        ⚠️ O desconto gerado é maior que o valor destinado ao cartão. Reveja a divisão da proposta.
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Resultado total */}
              <Card className="border-green-500/40 bg-green-500/5">
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><Award className="h-4 w-4" />Resultado total</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 text-sm">
                  <div><div className="text-xs text-muted-foreground">Valor original</div><div className="font-semibold">{brl(resultado.valor_original)}</div></div>
                  <div><div className="text-xs text-muted-foreground">Total pago cliente</div><div className="font-bold text-lg">{brl(resultado.resultado.total_pago_cliente)}</div></div>
                  <div><div className="text-xs text-muted-foreground">Desconto total concedido</div><div className="font-semibold text-green-700">{brl(resultado.resultado.desconto_total_concedido)}</div></div>
                  <div><div className="text-xs text-muted-foreground">Taxa efetiva cartão</div><div className="font-semibold text-orange-600">{brl(resultado.resultado.taxa_efetiva_cartao)}</div></div>
                  <div><div className="text-xs text-muted-foreground">Líquido total</div><div className="font-bold text-green-700 text-lg" data-testid="mix-liq-total">{brl(resultado.resultado.valor_liquido_total)}</div></div>
                  <div><div className="text-xs text-muted-foreground">Perda %</div><div className="font-semibold">{pct(resultado.resultado.perda_percentual)}</div></div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* ==== SUB-ABA REVERSA ==== */}
        <TabsContent value="reverse" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><RefreshCw className="h-4 w-4" />Quero receber X líquido total</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs">Líquido total desejado (R$)</Label>
                <Input type="number" step="0.01" placeholder="0,00" value={liqDesejado}
                  onChange={(e) => setLiqDesejado(e.target.value)} data-testid="mix-input-liq-desejado" />
              </div>
              <div>
                <Label className="text-xs">Valor à vista (R$)</Label>
                <Input type="number" step="0.01" placeholder="0,00" value={valorAvista}
                  onChange={(e) => setValorAvista(e.target.value)} data-testid="mix-input-avista-rev" />
              </div>
              <div>
                <Label className="text-xs">Forma à vista</Label>
                <Select value={formaAvista} onValueChange={setFormaAvista}>
                  <SelectTrigger data-testid="mix-select-forma-avista-rev"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FORMAS_A_VISTA.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Bandeira</Label>
                <Select value={bandeira} onValueChange={setBandeira}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{BANDEIRAS.map(b => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Parcelas</Label>
                <Select value={String(parcelas)} onValueChange={(v) => setParcelas(parseInt(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: MAX_PARCELAS_MISTO }).map((_, i) => (
                      <SelectItem key={i + 1} value={String(i + 1)}>{i + 1}x</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2 lg:col-span-4 flex justify-end">
                <Button onClick={simularReverso} disabled={loading} data-testid="mix-btn-simular-reverso">
                  <Calculator className="h-4 w-4 mr-2" />{loading ? 'Calculando...' : 'Calcular'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {resultadoReverso && (
            <Card className="border-green-500/40 bg-green-500/5">
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="h-4 w-4" />Resultado</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 text-sm">
                <div><div className="text-xs text-muted-foreground">Líquido desejado</div><div className="font-semibold">{brl(resultadoReverso.valor_liquido_total_desejado)}</div></div>
                <div><div className="text-xs text-muted-foreground">Entrada</div><div className="font-semibold">{brl(resultadoReverso.valor_a_vista)}</div></div>
                <div><div className="text-xs text-muted-foreground">Original cartão</div><div className="font-semibold">{brl(resultadoReverso.valor_original_cartao)}</div></div>
                <div><div className="text-xs text-muted-foreground">Desconto gerado</div><div className="font-semibold text-green-700">- {brl(resultadoReverso.desconto_gerado)}</div></div>
                <div><div className="text-xs text-muted-foreground">Cobrar no cartão</div><div className="font-bold text-green-700 text-base" data-testid="mix-rev-cobrar">{brl(resultadoReverso.valor_final_cartao)}</div></div>
                <div><div className="text-xs text-muted-foreground">{resultadoReverso.parcelas}× de</div><div className="font-semibold">{brl(resultadoReverso.valor_parcela)}</div></div>
                <div className="col-span-full text-sm">
                  Cliente paga <span className="font-semibold">{brl(resultadoReverso.valor_pago_cliente)}</span>, empresa recebe <span className="font-bold text-green-700">{brl(resultadoReverso.valor_liquido_total)}</span>.
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ==== SUB-ABA COMPARAR ==== */}
        <TabsContent value="compare" className="space-y-4">
          {commonDadosVenda}
          <Card>
            <CardHeader><CardTitle className="text-base">Configuração da comparação</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Bandeira</Label>
                <Select value={bandeira} onValueChange={setBandeira}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{BANDEIRAS.map(b => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Percentuais de entrada (separados por vírgula)</Label>
                <Input value={percsCompare} onChange={(e) => setPercsCompare(e.target.value)}
                  placeholder="10,20,30,40,50" data-testid="mix-input-percs-compare" />
              </div>
              <div>
                <Label className="text-xs">Parcelas máximas</Label>
                <Input type="number" min="1" max={MAX_PARCELAS_MISTO} value={parcMaxCompare}
                  onChange={(e) => setParcMaxCompare(e.target.value)} data-testid="mix-input-parc-max" />
              </div>
              <div className="sm:col-span-3 flex justify-end">
                <Button onClick={compararEntradas} disabled={loading} data-testid="mix-btn-comparar">
                  <LayoutGrid className="h-4 w-4 mr-2" />{loading ? 'Comparando...' : 'Comparar entrada e parcelas'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {resultadoCompare?.combinacoes?.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base">Combinações ({resultadoCompare.combinacoes.length})</CardTitle></CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="w-full text-xs sm:text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-2">À vista</th>
                      <th className="text-right py-2 px-2">Parcelas</th>
                      <th className="text-right py-2 px-2">Taxa</th>
                      <th className="text-right py-2 px-2">Desconto gerado</th>
                      <th className="text-right py-2 px-2">Cartão original</th>
                      <th className="text-right py-2 px-2">Cartão final</th>
                      <th className="text-right py-2 px-2">Parcela</th>
                      <th className="text-right py-2 px-2">Total pago</th>
                      <th className="text-right py-2 px-2">Líquido total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resultadoCompare.combinacoes.map((c, i) => {
                      const eMaiorLiq = i === resultadoCompare.idx_maior_liquido;
                      const eMenorTaxa = i === resultadoCompare.idx_menor_taxa;
                      const eMelhorCliente = i === resultadoCompare.idx_melhor_cliente;
                      return (
                        <tr key={i} className={eMaiorLiq ? 'bg-green-50 dark:bg-green-950/20 font-semibold' : ''}
                            data-testid={`mix-compare-row-${c.percentual_entrada}-${c.parcelas}`}>
                          <td className="py-2 px-2">
                            {c.percentual_entrada}% ({brl(c.valor_a_vista)})
                            {eMaiorLiq && <Badge className="ml-1 bg-green-600 text-[10px]">Maior líquido</Badge>}
                            {eMenorTaxa && !eMaiorLiq && <Badge className="ml-1 bg-blue-600 text-[10px]">Menor taxa</Badge>}
                            {eMelhorCliente && <Badge variant="outline" className="ml-1 text-[10px]">Melhor cliente</Badge>}
                          </td>
                          <td className="text-right py-2 px-2">{c.parcelas}×</td>
                          <td className="text-right py-2 px-2 text-orange-600">{pct(c.taxa_percentual)}</td>
                          <td className="text-right py-2 px-2 text-green-700">- {brl(c.desconto_gerado)}</td>
                          <td className="text-right py-2 px-2">{brl(c.valor_original_cartao)}</td>
                          <td className="text-right py-2 px-2 font-semibold">{brl(c.valor_final_cartao)}</td>
                          <td className="text-right py-2 px-2">{brl(c.valor_parcela)}</td>
                          <td className="text-right py-2 px-2">{brl(c.total_pago_cliente)}</td>
                          <td className="text-right py-2 px-2 text-green-700 font-semibold">{brl(c.valor_liquido_total)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ==== SUB-ABA COMPARAR CENÁRIOS (2 lado-a-lado) ==== */}
        <TabsContent value="scenarios" className="space-y-4">
          <ScenariosComparison
            valorOriginal={valorOriginal} setValorOriginal={setValorOriginal}
            bandeira={bandeira} setBandeira={setBandeira}
            formaAvista={formaAvista} setFormaAvista={setFormaAvista}
          />
        </TabsContent>

        {/* ==== SUB-ABA MAX DESCONTO ==== */}
        <TabsContent value="maxDiscount" className="space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Quanto posso dar de desconto?</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs">Valor original (R$)</Label>
                <Input type="number" step="0.01" value={valorOriginal} onChange={(e) => setValorOriginal(e.target.value)}
                  data-testid="mix-md-valor-original" />
              </div>
              <div>
                <Label className="text-xs">% à vista</Label>
                <Input type="number" step="0.01" value={percentualAvista}
                  onChange={(e) => onPercentualAvista(e.target.value)} data-testid="mix-md-pct-avista" />
              </div>
              <div>
                <Label className="text-xs">Bandeira</Label>
                <Select value={bandeira} onValueChange={setBandeira}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{BANDEIRAS.map(b => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Parcelas do restante</Label>
                <Select value={String(parcelas)} onValueChange={(v) => setParcelas(parseInt(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: MAX_PARCELAS_MISTO }).map((_, i) => (
                      <SelectItem key={i + 1} value={String(i + 1)}>{i + 1}x</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Líquido mínimo desejado (R$)</Label>
                <Input type="number" step="0.01" value={liqMinimo} onChange={(e) => setLiqMinimo(e.target.value)}
                  data-testid="mix-md-liq-min" />
              </div>
              <div className="sm:col-span-2 lg:col-span-4 flex justify-end">
                <Button onClick={calcularMaxDisc} disabled={loading} data-testid="mix-btn-max-desc">
                  <Calculator className="h-4 w-4 mr-2" />{loading ? 'Calculando...' : 'Calcular desconto máximo'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {resultadoMaxDisc && (
            <Card className={resultadoMaxDisc.atendido ? 'border-green-500/40 bg-green-500/5' : 'border-red-500/40 bg-red-500/5'}>
              <CardHeader><CardTitle className="text-base">{resultadoMaxDisc.atendido ? 'Desconto máximo permitido' : 'Impossível atingir o líquido mínimo'}</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div><div className="text-xs text-muted-foreground">Desconto máximo</div><div className="font-bold text-lg" data-testid="mix-md-desc-max">{pct(resultadoMaxDisc.desconto_maximo_percentual)}</div><div className="text-xs">{brl(resultadoMaxDisc.desconto_maximo_valor)}</div></div>
                <div><div className="text-xs text-muted-foreground">Líquido mínimo</div><div className="font-semibold">{brl(resultadoMaxDisc.liquido_minimo_solicitado)}</div></div>
                <div><div className="text-xs text-muted-foreground">Líquido atingido</div><div className="font-semibold text-green-700">{brl(resultadoMaxDisc.liquido_atingido)}</div></div>
                <div><div className="text-xs text-muted-foreground">Valor final da venda</div><div className="font-semibold">{brl(resultadoMaxDisc.simulacao?.valor_final)}</div></div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ============================================================
// Wrapper standalone da Análise IA para aba principal (state próprio)
// ============================================================
function AiAnalysisStandalone() {
  const [valorOriginal, setValorOriginal] = useState('');
  const [bandeira, setBandeira] = useState('mastercard');
  const [formaAvista, setFormaAvista] = useState('pix');
  const [valorAvista, setValorAvista] = useState('');
  const [parcelas, setParcelas] = useState(10);
  return (
    <AiAnalysisPanel
      valorOriginal={valorOriginal} setValorOriginal={setValorOriginal}
      bandeira={bandeira} setBandeira={setBandeira}
      formaAvista={formaAvista} setFormaAvista={setFormaAvista}
      valorAvista={valorAvista} setValorAvista={setValorAvista}
      parcelas={parcelas} setParcelas={setParcelas}
    />
  );
}

export default function SimuladorCartao() {
  const [modo, setModo] = useState('simulate'); // simulate | reverse
  const [valorOriginal, setValorOriginal] = useState('');
  const [descontoPercentual, setDescontoPercentual] = useState('');
  const [descontoValor, setDescontoValor] = useState('');
  const [bandeira, setBandeira] = useState('mastercard');
  const [tipo, setTipo] = useState('credito');
  const [parcelas, setParcelas] = useState(1);
  const [valorLiquidoDesejado, setValorLiquidoDesejado] = useState('');
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [ordenacao, setOrdenacao] = useState('parcelas'); // parcelas | liquido | taxa | cliente

  const valorFinalPreview = useMemo(() => {
    const vo = parseFloat(valorOriginal) || 0;
    const dv = parseFloat(descontoValor) || 0;
    const dp = parseFloat(descontoPercentual) || 0;
    if (dv > 0) return Math.max(0, vo - dv);
    if (dp > 0) return Math.max(0, vo - (vo * dp / 100));
    return vo;
  }, [valorOriginal, descontoValor, descontoPercentual]);

  const onDescontoPct = (v) => {
    setDescontoPercentual(v);
    const vo = parseFloat(valorOriginal) || 0;
    const p = parseFloat(v) || 0;
    setDescontoValor(vo > 0 && p > 0 ? (vo * p / 100).toFixed(2) : '');
  };
  const onDescontoValor = (v) => {
    setDescontoValor(v);
    const vo = parseFloat(valorOriginal) || 0;
    const dv = parseFloat(v) || 0;
    setDescontoPercentual(vo > 0 && dv > 0 ? ((dv / vo) * 100).toFixed(2) : '');
  };

  const simular = useCallback(async () => {
    const vo = parseFloat(valorOriginal) || 0;
    if (modo === 'simulate' && vo <= 0) { toast.error('Informe o valor da venda'); return; }
    if (modo === 'reverse' && (parseFloat(valorLiquidoDesejado) || 0) <= 0) { toast.error('Informe o valor líquido desejado'); return; }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        modo,
        valor_original: vo,
        desconto_percentual: parseFloat(descontoPercentual) || 0,
        desconto_valor: parseFloat(descontoValor) || 0,
        bandeira,
        tipo,
        parcelas: parseInt(parcelas) || 1,
        valor_liquido_desejado: parseFloat(valorLiquidoDesejado) || null,
      };
      const res = await axios.post(`${API}/card-simulator/simulate`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setResultado(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao simular');
    } finally {
      setLoading(false);
    }
  }, [modo, valorOriginal, descontoPercentual, descontoValor, bandeira, tipo, parcelas, valorLiquidoDesejado]);

  useEffect(() => {
    if (valorFinalPreview > 0 || (modo === 'reverse' && (parseFloat(valorLiquidoDesejado) || 0) > 0)) {
      const t = setTimeout(() => simular(), 400);
      return () => clearTimeout(t);
    }
  }, [valorFinalPreview, bandeira, tipo, parcelas, modo, valorLiquidoDesejado, simular]);

  const comparacaoOrdenada = useMemo(() => {
    if (!resultado?.comparacao) return [];
    const arr = [...resultado.comparacao];
    if (ordenacao === 'liquido') arr.sort((a, b) => b.valor_liquido - a.valor_liquido);
    else if (ordenacao === 'taxa') arr.sort((a, b) => a.taxa_percentual - b.taxa_percentual);
    else if (ordenacao === 'cliente') arr.sort((a, b) => a.valor_final - b.valor_final);
    return arr;
  }, [resultado, ordenacao]);

  const melhorLiquidoValor = useMemo(() => {
    if (!resultado?.comparacao?.length) return 0;
    return Math.max(...resultado.comparacao.map(c => c.valor_liquido));
  }, [resultado]);

  return (
    <Layout>
    <div className="space-y-4 p-3 sm:p-4 max-w-6xl mx-auto" data-testid="simulador-cartao">
      <div className="flex items-center gap-2">
        <CreditCard className="h-6 w-6 text-primary" />
        <h1 className="text-xl sm:text-2xl font-bold">Simulador de Venda no Cartão</h1>
      </div>

      <Tabs value={modo} onValueChange={setModo}>
        <TabsList className="grid grid-cols-2 md:grid-cols-4 max-w-3xl">
          <TabsTrigger value="simulate" data-testid="tab-simulate">Simulação Normal</TabsTrigger>
          <TabsTrigger value="reverse" data-testid="tab-reverse">Simulação Reversa</TabsTrigger>
          <TabsTrigger value="mixed" data-testid="tab-mixed"><Split className="h-4 w-4 mr-1" />Pagamento Misto</TabsTrigger>
          <TabsTrigger value="ai" data-testid="tab-ai" className="bg-primary/10 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Sparkles className="h-4 w-4 mr-1" />Análise IA
          </TabsTrigger>
        </TabsList>

        <TabsContent value="simulate" className="space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Dados da venda</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Valor original da venda (R$)</Label>
                <Input type="number" step="0.01" placeholder="0,00" value={valorOriginal}
                  onChange={(e) => setValorOriginal(e.target.value)} data-testid="input-valor-original" />
              </div>
              <div>
                <Label className="text-xs">Desconto (%)</Label>
                <Input type="number" step="0.01" placeholder="0" value={descontoPercentual}
                  onChange={(e) => onDescontoPct(e.target.value)} data-testid="input-desconto-pct" />
              </div>
              <div>
                <Label className="text-xs">Desconto (R$)</Label>
                <Input type="number" step="0.01" placeholder="0,00" value={descontoValor}
                  onChange={(e) => onDescontoValor(e.target.value)} data-testid="input-desconto-valor" />
              </div>
              <div>
                <Label className="text-xs">Bandeira</Label>
                <Select value={bandeira} onValueChange={setBandeira}>
                  <SelectTrigger data-testid="select-bandeira"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mastercard">Mastercard</SelectItem>
                    <SelectItem value="visa">Visa</SelectItem>
                    <SelectItem value="elo">Elo</SelectItem>
                    <SelectItem value="amex">Amex</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Tipo</Label>
                <Select value={tipo} onValueChange={(v) => { setTipo(v); if (v === 'debito') setParcelas(1); }}>
                  <SelectTrigger data-testid="select-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="debito">Débito</SelectItem>
                    <SelectItem value="credito">Crédito</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Parcelas</Label>
                <Select value={String(parcelas)} onValueChange={(v) => setParcelas(parseInt(v))} disabled={tipo === 'debito'}>
                  <SelectTrigger data-testid="select-parcelas"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 12 }).map((_, i) => (
                      <SelectItem key={i + 1} value={String(i + 1)}>{i + 1}x</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2 lg:col-span-3 flex items-end justify-between gap-2">
                <div className="text-sm">
                  Valor final: <span className="font-semibold">{brl(valorFinalPreview)}</span>
                </div>
                <Button onClick={simular} disabled={loading} data-testid="btn-simular">
                  <Calculator className="h-4 w-4 mr-2" />{loading ? 'Simulando...' : 'Simular'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {resultado?.principal && (
            <Card className="border-primary/50 bg-primary/5">
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Award className="h-4 w-4" />Condição escolhida</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 text-sm">
                <div><div className="text-xs text-muted-foreground">Valor original</div><div className="font-semibold">{brl(resultado.valor_original)}</div></div>
                <div><div className="text-xs text-muted-foreground">Desconto</div><div className="font-semibold">{brl(resultado.desconto_valor)} ({pct(resultado.desconto_percentual)})</div></div>
                <div><div className="text-xs text-muted-foreground">Valor cobrado</div><div className="font-semibold text-primary">{brl(resultado.valor_final)}</div></div>
                <div><div className="text-xs text-muted-foreground">Parcelas</div><div className="font-semibold">{resultado.principal.parcelas}× {brl(resultado.principal.valor_parcela)}</div></div>
                <div><div className="text-xs text-muted-foreground">Taxa operadora</div><div className="font-semibold text-orange-600">{pct(resultado.principal.taxa_percentual)} = {brl(resultado.principal.taxa_valor)}</div></div>
                <div><div className="text-xs text-muted-foreground">Valor líquido</div><div className="font-bold text-green-600">{brl(resultado.principal.valor_liquido)}</div></div>
              </CardContent>
            </Card>
          )}

          {resultado?.comparacao?.length > 0 && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between gap-2 flex-wrap">
                <CardTitle className="text-base">Comparação de parcelas</CardTitle>
                <div className="flex items-center gap-2">
                  <Label className="text-xs">Ordenar por:</Label>
                  <Select value={ordenacao} onValueChange={setOrdenacao}>
                    <SelectTrigger className="w-44" data-testid="select-ordenacao"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="parcelas">Parcelas</SelectItem>
                      <SelectItem value="liquido">Maior líquido</SelectItem>
                      <SelectItem value="taxa">Menor taxa</SelectItem>
                      <SelectItem value="cliente">Menor valor p/ cliente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-2">Parcelas</th>
                        <th className="text-right py-2 px-2">Taxa</th>
                        <th className="text-right py-2 px-2">Taxa (R$)</th>
                        <th className="text-right py-2 px-2">Valor cliente</th>
                        <th className="text-right py-2 px-2">Parcela</th>
                        <th className="text-right py-2 px-2">Líquido</th>
                        <th className="text-right py-2 px-2">Perda</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comparacaoOrdenada.map((c, i) => {
                        const eMelhor = c.valor_liquido === melhorLiquidoValor;
                        const perdaAlta = c.perda_percentual > 7;
                        return (
                          <tr key={i} className={eMelhor ? 'bg-green-50 dark:bg-green-950/20 font-semibold' : ''} data-testid={`row-comparacao-${c.parcelas}`}>
                            <td className="py-2 px-2">
                              {c.parcelas}×
                              {eMelhor && <Badge className="ml-2 bg-green-600 text-xs">Melhor líquido</Badge>}
                            </td>
                            <td className={`text-right py-2 px-2 ${perdaAlta ? 'text-red-600' : ''}`}>{pct(c.taxa_percentual)}</td>
                            <td className="text-right py-2 px-2">{brl(c.taxa_valor)}</td>
                            <td className="text-right py-2 px-2">{brl(c.valor_final)}</td>
                            <td className="text-right py-2 px-2">{brl(c.valor_parcela)}</td>
                            <td className={`text-right py-2 px-2 ${eMelhor ? 'text-green-700' : ''}`}>{brl(c.valor_liquido)}</td>
                            <td className={`text-right py-2 px-2 ${perdaAlta ? 'text-red-600' : 'text-orange-600'}`}>{pct(c.perda_percentual)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="reverse" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <RefreshCw className="h-4 w-4" />Quero receber X de líquido — quanto cobrar?
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs">Valor líquido desejado (R$)</Label>
                <Input type="number" step="0.01" placeholder="0,00" value={valorLiquidoDesejado}
                  onChange={(e) => setValorLiquidoDesejado(e.target.value)} data-testid="input-liquido-desejado" />
              </div>
              <div>
                <Label className="text-xs">Bandeira</Label>
                <Select value={bandeira} onValueChange={setBandeira}>
                  <SelectTrigger data-testid="select-bandeira-rev"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mastercard">Mastercard</SelectItem>
                    <SelectItem value="visa">Visa</SelectItem>
                    <SelectItem value="elo">Elo</SelectItem>
                    <SelectItem value="amex">Amex</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Tipo</Label>
                <Select value={tipo} onValueChange={(v) => { setTipo(v); if (v === 'debito') setParcelas(1); }}>
                  <SelectTrigger data-testid="select-tipo-rev"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="debito">Débito</SelectItem>
                    <SelectItem value="credito">Crédito</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Parcelas</Label>
                <Select value={String(parcelas)} onValueChange={(v) => setParcelas(parseInt(v))} disabled={tipo === 'debito'}>
                  <SelectTrigger data-testid="select-parcelas-rev"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 12 }).map((_, i) => (
                      <SelectItem key={i + 1} value={String(i + 1)}>{i + 1}x</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {resultado?.reverso && (
            <Card className="border-green-500/50 bg-green-500/5">
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="h-4 w-4" />Resultado</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 text-sm">
                <div><div className="text-xs text-muted-foreground">Líquido desejado</div><div className="font-semibold">{brl(resultado.reverso.valor_liquido_desejado)}</div></div>
                <div><div className="text-xs text-muted-foreground">Precisa cobrar</div><div className="font-bold text-green-600 text-lg">{brl(resultado.reverso.valor_a_cobrar)}</div></div>
                <div><div className="text-xs text-muted-foreground">Acréscimo</div><div className="font-semibold">{brl(resultado.reverso.acrescimo_valor)} ({pct(resultado.reverso.acrescimo_percentual)})</div></div>
                <div><div className="text-xs text-muted-foreground">{resultado.reverso.parcelas}× de</div><div className="font-semibold">{brl(resultado.reverso.valor_parcela)}</div></div>
                <div><div className="text-xs text-muted-foreground">Taxa operadora</div><div>{pct(resultado.reverso.taxa_percentual)} = {brl(resultado.reverso.taxa_valor)}</div></div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="mixed" className="space-y-4">
          <MixedPaymentSection />
        </TabsContent>

        <TabsContent value="ai" className="space-y-4">
          <AiAnalysisStandalone />
        </TabsContent>
      </Tabs>
    </div>
    </Layout>
  );
}
